
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@/types/user";
import { getPapersByAuthorId } from "@/lib/api";
import { Paper } from "@/types/paper";

export const useAuthorData = (authorId: string | undefined) => {
  const [author, setAuthor] = useState<User | null>(null);
  const [papers, setPapers] = useState<Paper[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileHidden, setIsProfileHidden] = useState(false);

  useEffect(() => {
    if (authorId) {
      fetchAuthorData(authorId);
      fetchAuthorPapers(authorId);
    }
  }, [authorId]);

  const fetchAuthorData = async (id: string) => {
    try {
      setIsProfileHidden(false);
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', id)
        .maybeSingle(); // Use maybeSingle to handle RLS-filtered results gracefully

      if (error) {
        console.error('Error fetching author:', error);
        return;
      }

      if (!data) {
        // Profile either doesn't exist or is hidden by RLS (profile_visibility = false)
        setIsProfileHidden(true);
        return;
      }

      setAuthor(data);
    } catch (error) {
      console.error('Error in author fetch:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAuthorPapers = async (id: string) => {
    try {
      const authorPapers = await getPapersByAuthorId(id);
      setPapers(authorPapers);
    } catch (error) {
      console.error('Error fetching author papers:', error);
    }
  };

  return { author, papers, isLoading, isProfileHidden };
};
